import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { AllergyRecord } from '@models/allergy';
import type { GetFhirRecordParams } from '@models/fhir-resource';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useListAllergiesQuery,
  useGetAllergyQuery,
  usePostAllergyRecordMutation,
  useUpdateAllergyRecordMutation,
  useDeleteAllergyRecordMutation,
} from 'generated/allergy.generated';
import type {
  PostAllergyRecordMutationVariables,
  UpdateAllergyRecordMutationVariables,
  DeleteAllergyRecordMutation,
  DeleteAllergyRecordMutationVariables,
} from 'generated/allergy.generated';
import type { GetPatientHealthSummaryRecordsQuery } from 'generated/patient-record.generated';
import { GetPatientHealthSummaryRecordsDocument } from 'generated/patient-record.generated';

export const useGetAllergies = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListAllergiesQuery({
    variables: {
      input: {
        patient: patient_id,
        count: 1500,
        page: 1,
      },
    },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);

  const records = (data?.listAllergies.data ?? []).map(
    (r) => new AllergyRecord(r)
  );
  return { ...response, data: records };
};

export const useGetAllergy = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetAllergyQuery({
    variables: {
      input: {
        ...params,
        patient_id,
      },
    },
    skip:
      !patient_id ||
      !params.record_id ||
      params.record_id === 'add' ||
      Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getAllergy;
  return { ...response, data: record ? new AllergyRecord(record) : null };
};

export function useCreateAllergy(): [
  (
    params: PostAllergyRecordMutationVariables
  ) => Promise<FetchResult<AllergyRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = usePostAllergyRecordMutation();
  const loading = baseLoading;

  async function createAllergy(params: PostAllergyRecordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postAllergyRecord) {
          return;
        }
        const storeObject =
          response.postAllergyRecord as unknown as StoreObject;
        const allergy = params.input;
        try {
          cache.modify<{ listAllergies: CachedRecordList }>({
            fields: {
              listAllergies: (existing, { storeFieldName, toReference }) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === allergy.patient.reference) {
                  const obj = toReference(storeObject);
                  return {
                    ...existing,
                    data: [...existing.data, ...(obj ? [obj] : [])],
                  };
                }
                return existing;
              },
            },
          });
          cache.updateQuery<GetPatientHealthSummaryRecordsQuery>(
            {
              query: GetPatientHealthSummaryRecordsDocument,
              variables: {
                input: {
                  patient: allergy.patient.reference,
                },
                allergy: true,
              },
            },
            (data) => ({
              getPatientHealthSummaryRecords: {
                allergy: [
                  ...(data?.getPatientHealthSummaryRecords.allergy ?? []),
                  response.postAllergyRecord,
                ],
              },
            })
          );
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdAllergy = response.data?.postAllergyRecord;
    return {
      ...response,
      data: createdAllergy ? new AllergyRecord(createdAllergy) : undefined,
    };
  }
  return [createAllergy, loading];
}

export function useUpdateAllergy(): [
  (
    params: UpdateAllergyRecordMutationVariables
  ) => Promise<FetchResult<AllergyRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useUpdateAllergyRecordMutation();
  const loading = baseLoading;

  async function updateAllergy(params: UpdateAllergyRecordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedAllergy = response.data?.updateAllergyRecord;
    return {
      ...response,
      data: updatedAllergy ? new AllergyRecord(updatedAllergy) : undefined,
    };
  }
  return [updateAllergy, loading];
}

export function useDeleteAllergy(): [
  (
    params: DeleteAllergyRecordMutationVariables
  ) => Promise<FetchResult<DeleteAllergyRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useDeleteAllergyRecordMutation();
  const loading = baseLoading;

  async function deleteAllergy(params: DeleteAllergyRecordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteAllergyRecord) {
          return;
        }
        const { allergy_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'AllergyModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteAllergy, loading];
}
